/* Real implementation of IdentityClient against the AI21 identity service
 * (the same backend studio-web uses). Auth is the identity session cookie —
 * every request sends credentials — and failures are normalized to the repo's
 * `{ error: { code, message } }` envelope so friendlyError()/toApiError()
 * work uniformly.
 *
 * API quirk to keep in mind: the api-keys write endpoints take their inputs as
 * QUERY PARAMETERS, not JSON bodies (POST /identity/api-keys?workspace_id=…&
 * key_name=…, PATCH /identity/api-keys/{id}/update?key_name=…). That mirrors
 * studio-web's client; do not "fix" it to a body. */

import type { IdentityClient } from "./IdentityClient";
import type { ApiError, ApiErrorCode } from "../analytics";
import { PRODUCT } from "../product";
import {
  apiKeyFromServer,
  memberFromServer,
  workspaceFromServer,
  type ApiKey,
  type Invite,
  type NewWorkspace,
  type Organization,
  type OrgMember,
  type OrgRole,
  type ServerApiKey,
  type ServerOrgUser,
  type ServerWorkspace,
  type Workspace,
} from "./types";

function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return "unauthorized";

  if (status === 403) return "forbidden";

  if (status === 404) return "not_found";

  if (status === 400 || status === 409 || status === 422) return "bad_request";

  return "upstream";
}

/** The identity service's error bodies vary; try the common shapes. */
function messageFromBody(body: unknown, status: number): string {
  for (const field of ["error", "detail", "message"] as const) {
    const value = (body as Record<string, unknown>)?.[field];
    if (typeof value === "string" && value) return value;
  }
  return `Request failed (HTTP ${status}).`;
}

/** Default ui_metadata color for workspaces created without an explicit one
 *  (the field is required by the API). */
const FALLBACK_WORKSPACE_COLOR = "#a766fa";

export class HttpIdentityClient implements IdentityClient {
  private readonly baseUrl: string;

  /** @param baseUrl identity service origin, e.g. "https://api.ai21-stg.com". */
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  private async request<T>(
    path: string,
    init?: RequestInit,
  ): Promise<T | undefined> {
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        // The identity session cookie is the only auth this service takes.
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
    } catch (cause) {
      const error: ApiError = {
        code: "upstream",
        message: "Couldn't reach the identity service.",
        detail: String(cause),
      };
      throw { error };
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const error: ApiError = {
        code: codeForStatus(res.status),
        message: messageFromBody(body, res.status),
        detail: body,
      };
      throw { error };
    }

    if (res.status === 204) return undefined;

    return (await res.json().catch(() => undefined)) as T;
  }

  async listWorkspaces(): Promise<Workspace[]> {
    const data = await this.request<{ workspaces: ServerWorkspace[] }>(
      "/identity/workspaces",
    );
    return (data?.workspaces ?? []).map(workspaceFromServer);
  }

  async createWorkspace(input: NewWorkspace): Promise<Workspace> {
    const data = await this.request<ServerWorkspace>("/identity/workspaces", {
      method: "POST",
      body: JSON.stringify({
        name: input.name,
        workspace_type: input.type ?? "private",
        ui_metadata: { color: input.color ?? FALLBACK_WORKSPACE_COLOR },
      }),
    });
    return workspaceFromServer(data as ServerWorkspace);
  }

  async listApiKeys(workspaceId: string): Promise<ApiKey[]> {
    const params = new URLSearchParams({ workspace_id: workspaceId });
    const data = await this.request<{ api_keys: ServerApiKey[] }>(
      `/identity/api-keys?${params}`,
    );
    // Revoked keys stay in the payload; the UI only shows live ones.
    return (data?.api_keys ?? [])
      .filter((k) => k.status !== "REVOKED")
      .map(apiKeyFromServer);
  }

  async createApiKey(
    workspaceId: string,
    name: string,
    expiresAt?: string,
  ): Promise<ApiKey> {
    // Inputs go in the query string (see module comment).
    const params = new URLSearchParams({
      workspace_id: workspaceId,
      key_name: name,
    });
    if (expiresAt) {
      params.set("expiration_date", new Date(expiresAt).toISOString());
    }

    const data = await this.request<ServerApiKey>(
      `/identity/api-keys?${params}`,
      { method: "POST" },
    );
    return apiKeyFromServer(data as ServerApiKey);
  }

  async renameApiKey(keyId: string, name: string): Promise<void> {
    const params = new URLSearchParams({ key_name: name });
    await this.request(
      `/identity/api-keys/${encodeURIComponent(keyId)}/update?${params}`,
      { method: "PATCH" },
    );
  }

  async rotateApiKey(keyId: string): Promise<ApiKey> {
    const data = await this.request<ServerApiKey>(
      `/identity/api-keys/${encodeURIComponent(keyId)}/rotate`,
      { method: "PATCH" },
    );
    return apiKeyFromServer(data as ServerApiKey);
  }

  async revokeApiKey(keyId: string): Promise<void> {
    await this.request(`/identity/api-keys/${encodeURIComponent(keyId)}`, {
      method: "DELETE",
    });
  }

  async getOrganization(): Promise<Organization> {
    return (await this.request<Organization>(
      "/identity/organization",
    )) as Organization;
  }

  async listOrgMembers(): Promise<OrgMember[]> {
    const data = await this.request<ServerOrgUser[]>(
      "/identity/organization/users",
    );
    return (data ?? []).map(memberFromServer);
  }

  async inviteMember(email: string): Promise<void> {
    await this.request("/identity/organization/invite", {
      method: "POST",
      // `product` selects the invite email's branding (the SparkPost template
      // "invite-to-organization-tokenwise"). Must be exactly "tokenwise" —
      // the identity service 422s unknown values; backends without the field
      // yet simply ignore it.
      body: JSON.stringify({ email, product: PRODUCT }),
    });
  }

  async changeRole(email: string, role: OrgRole): Promise<void> {
    await this.request("/identity/organization/users", {
      method: "POST",
      body: JSON.stringify({ email, newRole: role }),
    });
  }

  async removeMember(organizationId: string, userId: string): Promise<void> {
    await this.request(
      `/identity/organization/${encodeURIComponent(organizationId)}/users/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    );
  }

  async getInvite(invitationId: string): Promise<Invite> {
    // Returns the inviting org as { id, name }; the service 403s if the invite
    // isn't for the signed-in user, 404s if it's unknown or already consumed.
    const org = await this.request<Organization>(
      `/identity/organization/invite/${encodeURIComponent(invitationId)}`,
    );
    return { invitationId, organization: org as Organization };
  }

  async acceptInvitation(invitationId: string): Promise<void> {
    await this.request(
      `/identity/organization/invite/${encodeURIComponent(invitationId)}/accept`,
      { method: "POST" },
    );
  }
}
